import { css } from '@emotion/react'
import { ReactElement, useState } from 'react'

import {
  errorTextColor,
  baseColor,
  primaryTextColor,
  secondaryGrayColor
} from '../../../../lib/styles/colors'
import { srOnlyCss } from '../../../../lib/styles/cssStyles'
import { subtitle1Css, tertiaryBodyCss, captionTextCss } from '../../../../lib/styles/typography'
import { BP_SMALL_DOWN } from '../../../../lib/styles/breakpoints'
import { LabelVisibility } from '../../../../types'

export function useFocusState(
  onFocus?: (event: any) => void,
  onBlur?: (event: any) => void,
  disabled?: boolean
): [boolean, (event: any) => void, (event: any) => void] {
  const [focused, setFocused] = useState(false)

  const handleFocus = (event: any) => {
    if (disabled) return

    setFocused(true)

    if (onFocus) {
      onFocus(event)
    }
  }

  const handleBlur = (event: any) => {
    if (disabled) return

    setFocused(false)

    if (onBlur) {
      onBlur(event)
    }
  }

  return [focused, handleFocus, handleBlur]
}

export type TextFieldBaseProps = {
  /** Children nodes to go between the label and the  input field */
  children?: React.ReactNode
  /** Class name to be applied to the div containing the children and input field */
  childrenInputDivClassName?: string
  /** Optional className applied to input parent container */
  className?: string
  /** Optional icon to show at the end of the input field on hover */
  endAdornment?: ReactElement
  /** Indicates field is an error state */
  error?: boolean
  /** Whether the control is focused */
  focused: boolean
  /** Helper text displayed underneath the input */
  helperText?: string
  /** id of control  */
  id: string
  /** The input component to render */
  inputComponent: React.ReactNode
  /** Label of control */
  label: string
  /** ClassName to be applied to the label */
  labelClassName?: string
  /** Label visibility behavior */
  labelVisibility?: LabelVisibility
  /** Character limit for the input field */
  maxLength?: number
  /** Placeholder text */
  placeholder?: string
  /** Indicates if the remaining text count should always be shown */
  showRemainingCount?: boolean
  /** Value of the control */
  value?: string | number
}

const commonCss = css`
  position: relative;
  margin: 8px 20px 8px 8px;

  // Hide spinners on number fields
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield; /* Firefox */
  }

  input,
  textarea,
  .quill-editor {
    outline: 0;
    border: 1px solid ${secondaryGrayColor};
    padding: 10px 0;
    width: 100%;
    text-overflow: ellipsis;
  }

  input:focus::placeholder,
  textarea:focus::placeholder,
  .quill-editor:focus-within::placeholder {
    color: transparent;
  }

  input ~ .border,
  textarea ~ .border,
  .quill-editor ~ .border {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${baseColor};
    transform: scaleX(0);
    padding-right: 8px;
  }

  textarea ~ .border {
    bottom: 5px;
  }

  .quill-editor ~ .border {
    bottom: 1px;
  }

  input:focus ~ .border,
  textarea:focus ~ .border,
  .quill-editor:focus-within ~ .border {
    width: 100%;
    transform: scaleX(1);
    transition: transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  }
`

const primaryCss = css`
  &.focus input,
  &:hover input,
  &.focus textarea,
  &:hover textarea,
  &.focus .quill-editor,
  &:hover .quill-editor {
    border-color: ${secondaryGrayColor};
    border-bottom-color: rgba(0, 0, 0, 0.6);
    box-shadow: none;
  }

  &:hover input:not(:focus),
  &:hover textarea:not(:focus),
  &:hover .quill-editor:not(:focus) {
    + #endAdornment {
      display: inline;
    }
  }

  input {
    height: 14px;
  }

  input,
  textarea,
  .quill-editor {
    ${tertiaryBodyCss};
    color: ${primaryTextColor};
    padding: 22px 4px 10px 8px;
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border: 1px solid ${secondaryGrayColor};
    border-bottom: 1px solid rgba(0, 0, 0, 0.3);
  }

  input ~ .border,
  textarea ~ .border,
  .quill-editor ~ .border {
    padding-right: 14px;
  }
`

const inputContainerCss = css`
  position: relative;
`

const labelShrinkCss = css`
  transform: translate(11px, 10px) scale(0.75);
`

const helperTextCss = css`
  margin: 4px 0 14px 0;
`

const helperTextErrorCss = css`
  color: ${errorTextColor};
`

const captionCss = css`
  ${captionTextCss}
  padding-left: 10px;
`

const remainingCountCss = css`
  ${captionCss}
  color: ${errorTextColor};
`

const endAdornmentCss = css`
  height: 0.01em;
  max-height: 2em;
  align-items: center;
  white-space: nowrap;
  position: relative;
  padding-top: 8px;
  padding-right: 16px;
  display: none;

  @media (${BP_SMALL_DOWN}) {
    padding-top: 0;
  }

  button {
    padding-left: 4px;
  }
`

export const TextFieldBase = ({
  children,
  childrenInputDivClassName,
  className,
  endAdornment,
  error,
  focused,
  helperText,
  id,
  inputComponent,
  label,
  labelClassName,
  labelVisibility = LabelVisibility.DEFAULT,
  maxLength,
  placeholder,
  showRemainingCount = false,
  value = ''
}: TextFieldBaseProps) => {
  const labelCss = css`
    ${subtitle1Css}
    line-height: 20px;
    color: ${error ? errorTextColor : '#6a6a6a'};
    top: 0;
    left: 0;
    position: absolute;
    z-index: 1;
    transform: translate(12px, 24px) scale(1);
    transform-origin: top left;
    transition: color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms,
      transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    pointer-events: none;
    margin-top: -8px;
    margin-left: -2px;
  `

  const labelFocusedCss = css`
    color: ${error ? errorTextColor : baseColor};
  `

  const inputWrapperCss = css`
    display: ${endAdornment ? 'inline-flex' : 'block'};
  `

  const errorInputCss = css`
    input ~ .border,
    textarea ~ .border {
      background-color: ${errorTextColor};
      width: 100%;
      transform: scaleX(1);
      transition: transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    }
  `

  const labelHiddenOnFilled =
    labelVisibility === LabelVisibility.VISIBLE_ON_FOCUS && value && !focused
  const labelHidden = labelVisibility === LabelVisibility.HIDDEN
  const hasValue = value || (typeof value === 'number' && value >= 0)
  const srOnlyLabel = labelHidden || labelHiddenOnFilled || (!focused && placeholder)

  const labelCssSets = [
    subtitle1Css,
    labelCss,
    focused && labelFocusedCss,
    (hasValue || focused) && labelShrinkCss,
    srOnlyLabel && srOnlyCss
  ]
  const helperTextSetsCss = [captionCss, helperTextCss, error && helperTextErrorCss]

  const inputCssSets = [commonCss, primaryCss, error && errorInputCss]

  const getRemainingChars = () => {
    if (showRemainingCount && maxLength && value !== undefined && typeof value === 'string') {
      return maxLength - value.length
    }
    return undefined
  }
  const remainingCharacters = getRemainingChars()
  const remainingCss =
    remainingCharacters !== undefined && remainingCharacters <= 5 ? remainingCountCss : captionCss

  return (
    <div>
      <div className={className} css={inputCssSets}>
        <div css={inputContainerCss}>
          <label className={labelClassName} css={labelCssSets} htmlFor={id} id={`${id}-label`}>
            {label}
          </label>
          <div css={inputWrapperCss} className={childrenInputDivClassName}>
            {children}
            {inputComponent}
            {endAdornment && (
              <div className="endAdornment" id="endAdornment" css={endAdornmentCss}>
                {endAdornment}
              </div>
            )}
            <span className="border" />
          </div>
        </div>
      </div>
      {helperText && (
        <p aria-live="polite" css={helperTextSetsCss}>
          {helperText}
        </p>
      )}
      {showRemainingCount && (
        // WCAG 4.1.3: Add aria-live for character count announcements
        <div
          className="rename"
          css={remainingCss}
          aria-live="polite"
          aria-atomic="true"
        >{`${remainingCharacters} ${
          remainingCharacters === 1 ? 'character' : 'characters'
        } remaining`}</div>
      )}
    </div>
  )
}

export default TextFieldBase
